import { weekDate, weekModel } from "@aybuben/client/screens/week.ts";
import { STAGE_HY } from "@aybuben/client/tree/stage-names.ts";
import type { LangId } from "@aybuben/core/lang.ts";
import type { TreeState } from "@aybuben/core/progress/tree.ts";
import { Ribbon, Share2 } from "lucide-react";
import { useRef } from "react";

import { useCourse } from "../content/context.tsx";
import { useStrings } from "../i18n/index.tsx";
import { COL } from "../theme.ts";
import { Btn, CloseBtn, G, Sheet } from "./primitives.tsx";
import { STAGE_KEY, Tree } from "./tree/index.tsx";
import { Scene } from "./tree/Scene.tsx";

interface WeekSheetProps {
	/** The tree when the week's first visit began, and now. */
	from: TreeState;
	to: TreeState;
	/** When `from` was taken. */
	since: number;
	lang: LangId;
	seed?: number;
	onClose: () => void;
	onShare: (scene: HTMLElement) => void;
}

/** Once a week, what the tree gained since the last one: gains only, never a tally of days. */
export function WeekSheet({ from, to, since, lang, seed, onClose, onShare }: WeekSheetProps) {
	const t = useStrings();
	const course = useCourse();
	const scene = useRef<HTMLDivElement>(null);
	const { grew, closer, ripened, tied } = weekModel(from, to);
	const date = weekDate(since, lang);
	return (
		<Sheet onClose={onClose}>
			<div className="flex items-start justify-between gap-3">
				<div>
					<div className="text-2xl leading-tight font-extrabold">{t("week.title")}</div>
					<div className="text-sm" style={{ color: COL.mute }}>
						{t("week.since", { date })}
					</div>
				</div>
				<CloseBtn onClick={onClose} />
			</div>
			<Scene
				ref={scene}
				season={to.season}
				stage={to.stage}
				className="mt-3 h-56 w-full rounded-3xl"
			>
				<Tree tree={to} from={from} variant="full" seed={seed} />
			</Scene>
			<div className="mt-4 text-center" data-testid="week-growth">
				{grew ? (
					<>
						<div className="font-extrabold" style={{ color: COL.greenDark }}>
							{t("week.grewTo", { stage: t(STAGE_KEY[to.stage]) })}
						</div>
						<G size={22} style={{ color: COL.pom }}>
							{STAGE_HY[to.stage]}
						</G>
					</>
				) : (
					closer && (
						<div className="font-extrabold" style={{ color: COL.greenDark }}>
							{t("week.closer", { pct: closer.pct, stage: t(STAGE_KEY[closer.stage]) })}
						</div>
					)
				)}
			</div>
			<div className="mt-4 grid grid-cols-2 gap-2">
				<div
					className="flex flex-col items-center rounded-2xl py-3"
					style={{ background: COL.apricotSoft }}
				>
					<div className="text-2xl font-extrabold" style={{ color: COL.apricotDark }}>
						+{ripened}
					</div>
					<div className="text-xs font-semibold">{t("week.ripened")}</div>
				</div>
				<div
					className="flex flex-col items-center rounded-2xl py-3"
					style={{ background: COL.pomSoft }}
				>
					<div className="text-2xl font-extrabold" style={{ color: COL.pom }}>
						+{tied.length}
					</div>
					<div className="text-xs font-semibold">{t("week.ribbons")}</div>
				</div>
			</div>
			{tied.map((u) => (
				<div key={u} className="mt-2 flex items-center gap-2 text-sm font-semibold">
					<Ribbon size={16} color={COL.pom} aria-hidden />
					{t("moment.ribbon", { title: course.t(`${u}.title`) })}
				</div>
			))}
			<div className="mt-5 flex flex-col gap-3">
				<Btn onClick={() => scene.current && onShare(scene.current)} color={COL.lapis}>
					<span className="inline-flex items-center gap-2">
						<Share2 size={18} aria-hidden />
						{t("tree.share")}
					</span>
				</Btn>
				<Btn onClick={onClose} color={COL.pom}>
					{t("week.close")}
				</Btn>
			</div>
		</Sheet>
	);
}
